import { faQuestionCircle } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, darken, styled, Typography } from "@mui/material";
import { blue } from "@mui/material/colors";
import { FC, useMemo } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { ReportTemplateTypes } from "../../api/dtos";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleTablePremium from "../../components/tables/CollapsibleTablePremium";
import TitleBox from "../../components/TitleBox";
import { createCustomToolbarProps } from "../../components/utility/CustomToolbarProps";
import ReportPreviewContext from "../../contexts/ReportPreviewContext";
import useReportPreviewById from "../../hooks/data/useReportPreviewById";
import useReportTemplateById from "../../hooks/data/useReportTemplateById";
import useReportTemplateFieldsByTemplateId from "../../hooks/data/useReportTemplateFieldsByTemplateId";
import { useAssetsColumns, useColumnGroupingModel, useCommissionReportColumns, useContractsOverdueColumns, useGrossRealisationColumns, useLotSummaryColumns, useSalesAdviceToContractColumns, useSalesRateColumns, useSalesSummaryColumns, useStageLotCountsColumns, useTemplateFieldColumns, useUpcomingSettlementsColumns } from "./ReportColumnDefinitions";

const HelpBox = styled(Box) (({theme}) => ({
    backgroundColor: blue[100],
    borderWidth: "thick",
    borderColor: darken(blue[100], 0.1),
    borderRadius: 10,
    borderStyle: "solid",
    padding: theme.spacing(1),
}))

const StyledLink = styled(Link) (({theme}) => ({
    color: theme.palette.secondary.main,
    textDecoration: "none",
    fontWeight: "bold"
}))

const ReportPreview : FC = () => {
    const params = useParams();

    const reportId = Number(params.reportId);
    
    const contextData = useReportPreviewById(reportId);
    const {data: preview} = contextData;
    const {data: template} = useReportTemplateById(preview?.report?.templateId);
    const {data: fields} = useReportTemplateFieldsByTemplateId(preview?.report?.templateId);

    const customColumns = useTemplateFieldColumns(fields);
    const salesSummaryColumns = useSalesSummaryColumns(preview?.rows ?? []);
    const lotSummaryColumns = useLotSummaryColumns();
    const contractsOverdueColumns = useContractsOverdueColumns();
    const commissionReportColumns = useCommissionReportColumns();
    const salesAdviceToContractColumns = useSalesAdviceToContractColumns();
    const grossRealisationColumns = useGrossRealisationColumns(preview?.rows ?? []);
    const salesRateColumns = useSalesRateColumns(preview?.rows ?? []);
    const stageLotCountsColumns = useStageLotCountsColumns();
    const upcomingSettlementsColumns = useUpcomingSettlementsColumns();
    const assetsColumns = useAssetsColumns();

    const columns = useMemo(() => {
        switch(template?.reportTemplate?.templateType) {
            case ReportTemplateTypes.Custom:
                return customColumns;
            case ReportTemplateTypes.SalesSummary:
                return salesSummaryColumns;
            case ReportTemplateTypes.ContractsOverdue:
                return contractsOverdueColumns;
            case ReportTemplateTypes.LotSummary:
                return lotSummaryColumns;
            case ReportTemplateTypes.CommissionReport:
                return commissionReportColumns;
            case ReportTemplateTypes.SalesAdviceToContract:
                return salesAdviceToContractColumns;
            case ReportTemplateTypes.GrossRealisationReport:
                return grossRealisationColumns;
            case ReportTemplateTypes.SalesRateReport:
                return salesRateColumns;
            case ReportTemplateTypes.StageLotCounts:
                return stageLotCountsColumns;
            case ReportTemplateTypes.UpcomingSettlements:
                return upcomingSettlementsColumns;
            case ReportTemplateTypes.Assets:
                return assetsColumns;
        }
    }, [preview?.report, template, customColumns])

    
    const columnGroupingModel = useColumnGroupingModel(template?.reportTemplate?.templateType);
    
    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        fileName: `${preview?.report?.name ?? "Unknown Report"} - Preview`,
        exportTitle: `Email Only Reports`,
        includeDateTime: true
    }), []);

    return (
        <ReportPreviewContext.Provider value={contextData}>
            <SetLinks
                stageLinkEnabled={false}
                lotLinkEnabled={false}
            />
            <Outlet />
            <TitleBox
                sx={{
                    display: "flex",
                    // justifyContent: "flex-end",
                    gap: 2
                }}
            >
                <HelpBox>
                    <FontAwesomeIcon icon={faQuestionCircle} size="lg"/>
                    <Typography 
                        sx={{
                            display: "inline",
                            ml: 2
                        }} 
                        variant="body1"
                    >
                        This is a saved report with filters already applied. You can adjust filters in real time by viewing <StyledLink to={`/reports/templates/preview/${template?.reportTemplate?.id}`}>{template?.reportTemplate?.name ?? <>this template</>}</StyledLink>.
                    </Typography>
                </HelpBox>
                {
                    (template && template.reportTemplate?.templateType == ReportTemplateTypes.Assets) &&
                    <StyledButton
                        variant="contained"
                        LinkComponent={Link}
                        //@ts-expect-error
                        to={`/reports/preview/${reportId}/assets/create`}
                    >
                        Add Asset
                    </StyledButton>
                }
            </TitleBox>
            <CollapsibleTablePremium 
                density="compact"
                title={"Entries"} 
                loading={false} 
                columns={columns ?? []}
                disableColumnMenu={false}
                rows={preview?.rows?.map((row, index) => {return {id: row.Id ?? row.id ?? index, ...row}})}
                disableColumnResize={false}
                disableColumnSelector={false}
                columnGroupingModel={columnGroupingModel}
                disableColumnFilter={false}
                {...customToolbarProps}
            />
        </ReportPreviewContext.Provider>
    );
}

export default ReportPreview;